import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../components/Loader";
import NavigationDrawer from "../../../components/NavigationDrawer";
import SnackbarAlert from "../../../components/SnackBar";
import { setActiveTab } from "../../../lib/slices/activeTabSlice";
import { useBranches } from "../../../hooks/branch";
import { createBranch, updateBranch } from "../../../services/Branches";
import AddEditBranches from "./AddEditBranches";
import AllBranches from "./Branches";

const Branches = () => {
  const { allBranches, fetchBranches } = useBranches();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const { userPro } = useSelector((state) => state.userProSlice);
  const activeTab = useSelector((state) => state.activeTab.tabs);

  const [type, setType] = useState("add");
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [branchId, setBranchId] = useState("");
  const [snackbarProps, setSnackbarProps] = useState({
    open: false,
    type: "",
    message: "",
  });
  const [inputValues, setInputValues] = useState({
    cvv: "",
    businessLat: "",
    businessLng: "",
    aboutUs: "",
    contact: "",
    address: "",
    lastName: "",
    firstName: "",
    cardNumber: "",
    expiryDate: "",
    genderType: "",
    gaztNumber: "",
    accountType: "",
    displayName: "",
    venueImages: [],
    categories: [],
    timmings: [],
    cardHolderName: "",
    businessAddress: "",
    venueCoverImage: "",
  });

  useEffect(() => {
    fetchBranches();
  }, []);

  const handleChangeTab = (tab) =>
    dispatch(setActiveTab({ ...activeTab, branch: tab }));
  const handleSnackbarVal = (open, type, message) =>
    setSnackbarProps({ open, type, message });
  const handleCloseSnackbar = () => handleSnackbarVal(false, "", "");

  const handleEdit = (row) => {
    setType("edit");
    setBranchId(row?._id);
    handleChangeTab("addEditBranch");
    handleSetDataToFields(row);
  };

  const handleSetDataToFields = (data) => {
    setInputValues({
      cvv: data?.paymentDetails?.cvv,
      businessLat: data?.location?.coordinates[1],
      businessLng: data?.location?.coordinates[0],
      categories: data?.categoryType,
      aboutUs: data?.about,
      contact: data?.contactNumber,
      address: data?.billingDetails?.address,
      lastName: data?.billingDetails?.lastname,
      firstName: data?.billingDetails?.firstname,
      cardNumber: data?.paymentDetails?.accountNumber,
      expiryDate: data?.paymentDetails?.expiryDate,
      gaztNumber: data?.billingDetails?.gaztNumber,
      accountType: data?.billingDetails?.accountType,
      displayName: data?.displayName,
      genderType: data?.genderType,
      venueImages: data?.images,
      timmings: data?.timmings,
      cardHolderName: data?.paymentDetails?.cardHolderName,
      businessAddress: data?.location?.address,
      venueCoverImage: data?.coverImage,
    });
  };

  const handleCreateBranch = async () => {
    try {
      // Collect missing fields
      let missingFields = [];

      if (!inputValues?.displayName) missingFields.push("Display Name");
      if (!inputValues?.aboutUs) missingFields.push("About Us");
      if (!inputValues?.contact) missingFields.push("Contact Number");
      if (!inputValues?.categories) missingFields.push("Category Type");
      if (!user?._id) missingFields.push("Business ID");
      if (!userPro?._id) missingFields.push("Business Detail ID");
      if (!inputValues?.venueCoverImage) missingFields.push("Cover Image");
      if (!inputValues?.genderType) missingFields.push("Gender Type");

      // Location fields
      if (!inputValues?.businessAddress) missingFields.push("Business Address");
      if (!inputValues?.businessLng) missingFields.push("Business Longitude");
      if (!inputValues?.businessLat) missingFields.push("Business Latitude");

      if (!inputValues?.timmings) missingFields.push("Timings");
      if (!inputValues?.venueImages || inputValues?.venueImages.length === 0)
        missingFields.push("Venue Images");

      // Payment Details
      if (!inputValues?.cvv) missingFields.push("CVV");
      if (!inputValues?.expiryDate) missingFields.push("Expiry Date");
      if (!inputValues?.cardNumber) missingFields.push("Card Number");
      if (!inputValues?.cardHolderName) missingFields.push("Card Holder Name");

      // Billing Details
      if (!inputValues?.accountType) missingFields.push("Account Type");
      if (!inputValues?.firstName) missingFields.push("First Name");
      if (!inputValues?.lastName) missingFields.push("Last Name");
      if (!inputValues?.address) missingFields.push("Billing Address");
      if (!inputValues?.gaztNumber) missingFields.push("GAZT Number");

      // If any field is missing, show an error message
      if (missingFields.length > 0) {
        return handleSnackbarVal(
          true,
          "error",
          `Please fill in the following required fields: ${missingFields.join(
            ", "
          )}`
        );
      }

      // Prepare request payload
      const params = {
        displayName: inputValues?.displayName,
        about: inputValues?.aboutUs,
        contactNumber: inputValues?.contact,
        status: true,
        categoryType: inputValues.categories,
        businessId: user?._id,
        businessDetailId: userPro?._id,
        coverImage: inputValues?.venueCoverImage,
        genderType: inputValues?.genderType,
        location: {
          address: inputValues?.businessAddress,
          type: "Point",
          coordinates: [
            Number(inputValues?.businessLng),
            Number(inputValues?.businessLat),
          ],
        },
        timmings: inputValues?.timmings,
        images: inputValues?.venueImages,
        paymentDetails: {
          cvv: inputValues?.cvv,
          expiryDate: inputValues?.expiryDate,
          accountNumber: inputValues?.cardNumber,
          cardHolderName: inputValues?.cardHolderName,
        },
        billingDetails: {
          accountType: inputValues.accountType,
          firstname: inputValues.firstName,
          lastname: inputValues.lastName,
          address: inputValues?.address,
          gaztNumber: inputValues?.gaztNumber,
        },
      };

      setIsLoading(true);
      const response =
        type !== "edit"
          ? await createBranch(params)
          : await updateBranch(branchId, params);

      setIsLoading(false);
      handleSnackbarVal(
        true,
        response.status === 200 || response.status === 201
          ? "success"
          : "error",
        response?.data?.message
      );

      if (response.status === 200 || response.status === 201) {
        handleGoBack();
        fetchBranches();
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    handleChangeTab("branch");
    setCurrentStep(1);
    setInputValues({
      cvv: "",
      businessLat: "",
      businessLng: "",
      aboutUs: "",
      contact: "",
      address: "",
      lastName: "",
      firstName: "",
      cardNumber: "",
      expiryDate: "",
      gaztNumber: "",
      accountType: "",
      displayName: "",
      venueImages: [],
      timmings: [],
      cardHolderName: "",
      businessAddress: "",
      venueCoverImage: "",
    });
  };

  const handleSelect = (data) => {
    console.log(data, "data");

    setInputValues({
      ...inputValues,
      businessAddress: data?.label,
      businessLat: data?.latitude,
      businessLng: data?.longitude,
    });
    handleSuggestionSelect(data?.value?.place_id);
  };

  const handleSuggestionSelect = (suggestion) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: suggestion }, (results, status) => {
      if (status === "OK") {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
      } else {
      }
    });
  };
  return (
    <NavigationDrawer>
      {activeTab.branch === "branch" ? (
        <AllBranches
          setType={setType}
          handleEdit={handleEdit}
          allBranches={allBranches}
          handleChangeTab={handleChangeTab}
        />
      ) : activeTab.branch === "addEditBranch" ? (
        <AddEditBranches
          type={type}
          handleSelect={handleSelect}
          currentStep={currentStep}
          handleChangeTab={handleChangeTab}
          inputValues={inputValues}
          setCurrentStep={setCurrentStep}
          handleCreateBranch={handleCreateBranch}
          setInputValues={setInputValues}
        />
      ) : null}
      <SnackbarAlert
        handleCloseSnackbar={handleCloseSnackbar}
        snackbarProps={snackbarProps}
      />
      <Loader isLoading={isLoading} />
    </NavigationDrawer>
  );
};

export default Branches;
